import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  type Theme,
} from "@mui/material";
import { TenantPickerDialog } from "components/TenantPickerDialog";
import { MySubscribedOrganizationsQuery, TenantContactType } from "generated-graphql/graphql";
import { useState, type ReactNode } from "react";

import { ROLE_LABELS } from "./accountRoleLabels";
import { OverridesMarker } from "./OverridesMarker";
import { WIDGET_SX } from "./preferenceProvenance";

/**
 * The left side of a staff member's My preferences tab: "Default", then the organizations the
 * staff member is subscribed to. Picking one chooses whose settings the panel beside
 * it edits.
 *
 * The organization the page is open in is listed even when it is not subscribed, so
 * a staff member who arrived from a customer's page can always edit their settings
 * there and subscribe in one click.
 *
 * Built for a CSM with dozens of accounts: the count sits in the header, the
 * organizations scroll under a fixed Default, and a long list gets a name filter and
 * a way to narrow it to the organizations whose settings differ from the Default.
 */

export type SubscribedOrganization =
  MySubscribedOrganizationsQuery["mySubscribedOrganizations"][number];

/** Every organization, as opposed to one of them. */
export const ALL_ORGANIZATIONS = "ALL";

/** What the panel edits: every organization, or one by id. */
export type OrganizationSelection = typeof ALL_ORGANIZATIONS | string;

/** How many organizations show before the list scrolls, at the dense row height. */
const ORGANIZATIONS_MAX_HEIGHT = 400;

/** Past this many organizations the list offers its filters. */
const FILTER_FROM = 8;

/** A bar on the selected row's edge, over MUI's own selected tint. */
const SELECTED_EDGE_SX = {
  "&.Mui-selected": {
    boxShadow: (theme: Theme) => `inset 4px 0 0 ${theme.palette.primary.main}`,
  },
};

/** The organization's name with its chips on the same line; a long name truncates instead. */
function RowName({
  name,
  roles,
  notSubscribed,
  overridden,
}: {
  name: string;
  roles: readonly TenantContactType[];
  notSubscribed: boolean;
  overridden: boolean;
}) {
  if (roles.length === 0 && !notSubscribed && !overridden) {
    return name;
  }
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Box
        component="span"
        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {name}
      </Box>
      {roles.map((role) => (
        <Chip
          key={role}
          label={ROLE_LABELS[role]}
          size="small"
          variant="outlined"
          sx={{ flexShrink: 0 }}
        />
      ))}
      {notSubscribed && (
        <Chip
          label="Not subscribed"
          size="small"
          color="warning"
          variant="outlined"
          sx={{ flexShrink: 0 }}
        />
      )}
      {overridden && <OverridesMarker title="Some settings here differ from the default setting" />}
    </Stack>
  );
}

function OrganizationRow({
  name,
  roles,
  notSubscribed = false,
  overridden = false,
  selected,
  disabled,
  action,
  onSelect,
}: {
  name: string;
  roles: readonly TenantContactType[];
  notSubscribed?: boolean;
  overridden?: boolean;
  selected: boolean;
  disabled: boolean;
  /** `label` prefixes the organization's name for screen readers; `tooltip` says what it does. */
  action: { label: string; tooltip: string; icon: ReactNode; onClick: () => void };
  onSelect: () => void;
}) {
  // The action sits beside the row's button rather than inside it: a button within a
  // button is a control a screen reader cannot reach on its own.
  return (
    <ListItem
      disablePadding
      secondaryAction={
        <Tooltip title={action.tooltip}>
          {/* A disabled button fires no pointer events for the tooltip to hang off. */}
          <span>
            <IconButton
              size="small"
              aria-label={`${action.label} ${name}`}
              disabled={disabled}
              onClick={action.onClick}
            >
              {action.icon}
            </IconButton>
          </span>
        </Tooltip>
      }
      sx={{ "& .MuiListItemSecondaryAction-root": { right: 8 } }}
    >
      <ListItemButton
        selected={selected}
        onClick={onSelect}
        disabled={disabled}
        sx={{ pr: 6, ...SELECTED_EDGE_SX }}
      >
        <ListItemText
          primary={
            <RowName
              name={name}
              roles={roles}
              notSubscribed={notSubscribed}
              overridden={overridden}
            />
          }
          primaryTypographyProps={{
            component: "div",
            variant: "body2",
            fontWeight: selected ? 600 : 400,
          }}
        />
      </ListItemButton>
    </ListItem>
  );
}

/** The open organization when it is not subscribed, which the list puts first. */
function unsubscribedCurrent(
  organizations: readonly SubscribedOrganization[] | undefined,
  currentTenant: { id: string; name: string } | undefined
): { id: string; name: string } | null {
  if (!currentTenant || !organizations) {
    return null;
  }
  return organizations.some((organization) => organization.tenantId === currentTenant.id)
    ? null
    : currentTenant;
}

/** Heads the whole list; a band, so it never reads as a row to pick. */
const SUBSCRIPTIONS_HEADER_SX = {
  display: "flex",
  justifyContent: "space-between",
  px: 2,
  bgcolor: "action.hover",
  color: "text.secondary",
  fontSize: "0.75rem",
  fontWeight: 600,
  letterSpacing: "0.06em",
  lineHeight: "32px",
  textTransform: "uppercase",
  borderBottom: 1,
  borderColor: "divider",
} as const;

/** The name filter and the overridden-only toggle a long list offers. */
function OrganizationFilters({
  query,
  onQuery,
  overriddenOnly,
  onOverriddenOnly,
  overriddenCount,
}: {
  query: string;
  onQuery: (query: string) => void;
  overriddenOnly: boolean;
  onOverriddenOnly: (overriddenOnly: boolean) => void;
  overriddenCount: number;
}) {
  return (
    <Stack spacing={1} sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
      <TextField
        size="small"
        placeholder="Filter organizations"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        slotProps={{ htmlInput: { "aria-label": "Filter organizations" } }}
      />
      {overriddenCount > 0 && (
        <Chip
          icon={<TuneOutlined />}
          label={`Has Overrides (${overriddenCount})`}
          size="small"
          color={overriddenOnly ? "primary" : "default"}
          variant={overriddenOnly ? "filled" : "outlined"}
          aria-pressed={overriddenOnly}
          onClick={() => onOverriddenOnly(!overriddenOnly)}
          sx={{ alignSelf: "flex-start" }}
        />
      )}
    </Stack>
  );
}

export function SubscribedOrganizationList({
  organizations,
  loading,
  currentTenant,
  selection,
  disabled,
  onSelect,
  onSubscribe,
}: {
  organizations: readonly SubscribedOrganization[] | undefined;
  loading: boolean;
  /** The organization the page is open in. */
  currentTenant: { id: string; name: string } | undefined;
  selection: OrganizationSelection;
  /** True while a change to the list or to the settings is in flight. */
  disabled: boolean;
  onSelect: (selection: OrganizationSelection) => void;
  onSubscribe: (tenantId: string, subscribed: boolean) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [overriddenOnly, setOverriddenOnly] = useState(false);

  const subscribed = organizations ?? [];
  const showFilters = subscribed.length > FILTER_FROM;
  const overriddenCount = subscribed.filter((organization) => organization.hasOverrides).length;
  const matches = (name: string) =>
    !showFilters || name.toLowerCase().includes(query.trim().toLowerCase());
  const shown = subscribed.filter(
    (organization) =>
      matches(organization.name) && !(showFilters && overriddenOnly && !organization.hasOverrides)
  );
  const unsubscribed = unsubscribedCurrent(organizations, currentTenant);
  const current =
    unsubscribed && matches(unsubscribed.name) && !(showFilters && overriddenOnly)
      ? unsubscribed
      : null;

  return (
    <Paper elevation={0} sx={{ ...WIDGET_SX, p: 0, width: "100%" }}>
      <Box sx={SUBSCRIPTIONS_HEADER_SX}>
        <span>Subscriptions</span>
        {organizations && <span>{subscribed.length}</span>}
      </Box>
      <List dense disablePadding>
        <ListItemButton
          selected={selection === ALL_ORGANIZATIONS}
          onClick={() => onSelect(ALL_ORGANIZATIONS)}
          disabled={disabled}
          sx={{ alignItems: "flex-start", ...SELECTED_EDGE_SX }}
        >
          <ListItemText
            primary="Default"
            primaryTypographyProps={{
              variant: "body2",
              fontWeight: selection === ALL_ORGANIZATIONS ? 600 : 400,
            }}
          />
        </ListItemButton>
      </List>
      <Divider />

      {showFilters && (
        <OrganizationFilters
          query={query}
          onQuery={setQuery}
          overriddenOnly={overriddenOnly}
          onOverriddenOnly={setOverriddenOnly}
          overriddenCount={overriddenCount}
        />
      )}

      <Box sx={{ maxHeight: ORGANIZATIONS_MAX_HEIGHT, overflowY: "auto" }}>
        <List dense disablePadding aria-label="Organizations">
          {current && (
            <OrganizationRow
              name={current.name}
              roles={[]}
              notSubscribed
              selected={selection === current.id}
              disabled={disabled}
              action={{
                label: "Subscribe to",
                tooltip: "Subscribe to get this organization's notifications",
                icon: <AddIcon fontSize="small" />,
                onClick: () => onSubscribe(current.id, true),
              }}
              onSelect={() => onSelect(current.id)}
            />
          )}

          {loading && !organizations ? (
            <Box sx={{ px: 2, py: 1 }}>
              <Skeleton height={32} />
              <Skeleton height={32} />
            </Box>
          ) : (
            shown.map((organization) => (
              <OrganizationRow
                key={organization.tenantId}
                name={organization.name}
                roles={organization.accountRoles}
                overridden={organization.hasOverrides}
                selected={selection === organization.tenantId}
                disabled={disabled}
                action={{
                  label: "Unsubscribe from",
                  tooltip: "Unsubscribe to stop getting this organization's notifications",
                  icon: <CloseIcon fontSize="small" />,
                  onClick: () => onSubscribe(organization.tenantId, false),
                }}
                onSelect={() => onSelect(organization.tenantId)}
              />
            ))
          )}
        </List>
        {organizations && subscribed.length === 0 && (
          <Alert severity="warning" sx={{ m: 1 }}>
            You aren't subscribed to any organizations, so only notifications about your own work
            reach you.
          </Alert>
        )}
        {showFilters && shown.length === 0 && !current && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
            No organizations match.
          </Typography>
        )}
      </Box>

      <Box sx={{ p: 1, borderTop: 1, borderColor: "divider" }}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setPickerOpen(true)}
          disabled={disabled}
        >
          Add organization
        </Button>
      </Box>

      <TenantPickerDialog
        open={pickerOpen}
        title="Subscribe to an organization"
        onClose={() => setPickerOpen(false)}
        onTenantSelected={(tenant) => {
          setPickerOpen(false);
          onSubscribe(tenant.id, true);
        }}
      />
    </Paper>
  );
}
